import clsx from 'clsx';
import { cloneElement, useState, useRef, useEffect, Children } from 'react';
import type { ReactNode, MouseEvent, ChangeEvent } from 'react';
import { type Field, validators } from '../../utils';
import { Icon } from '../icon/icon.component';
import { Text } from '../text/text.component';
import { FieldError } from '../field-error/field-error.component';
import { Menu } from '../menu/menu.component';
import { SelectOption } from '../select-option/select-option.component';
import { useDebounce } from '../../hooks/useDebounce';
import { type Style, mergeStyle } from './autocomplete.module.css';

interface Props<T> {
  icon?: string;
  value?: T;
  initialDisplayValue?: string;
  placeholder?: string;
  formField?: Field<T> | Field<undefined>;
  required?: boolean;
  children?: ReactNode;
  style?: Style;
  label?: string;
  hint?: string;
  error?: string;
  type?: string;
  autofocus?: boolean;
  disabled?: boolean;
  hideIcon?: boolean;
  autoUpdatePosition?: boolean;
  onChange?: (value?: T) => void;
  onSearch?: (value?: string) => void;
  onEnter?: () => void;
}

export function Autocomplete<T>(props: Props<T>) {

  const {
    icon = 'search',
    label,
    placeholder,
    children = [],
    formField: field,
    required = false,
    hint,
    autofocus,
    disabled = false,
    error,
    hideIcon,
    initialDisplayValue = '',
    value: initValue,
    style: customStyle,
    autoUpdatePosition = false,
    onChange,
    onSearch
  } = props;

  const style = mergeStyle(customStyle);
  const value = field ? field.value : initValue;
  const ref = useRef<HTMLDivElement>(null);
  const debounce = useDebounce();
  const [openPopup, setOpenPopup] = useState(false);
  const [displayValue, setDisplayValue] = useState(initialDisplayValue);

  useEffect(() => {

    const childrenList = Array.isArray(children) ? children : [children];

    const selectedChild = childrenList.find((child: any) => {
      if (
        child && 
        typeof child === 'object' && 
        child?.type === SelectOption && 
        JSON.stringify(child.props.value) === JSON.stringify(value)
      ) {
        return child;
      }
    }) as any;

    setDisplayValue(selectedChild?.props?.label ?? initialDisplayValue ?? '');
  }, []);

  useEffect(() => {
    if (required) {
      field?.addValidators([validators.required]);
    }
  }, [required]);

  useEffect(() => {
    field?.onClear(() => clear());
    return () => {
      field?.onClear(undefined);
      field?.addValidators([]);
    };
  }, []);

  function handleClick() {

    if (disabled) return;

    setOpenPopup(state => !state);
    onSearch?.(displayValue);
  }

  function handleClear(event?: MouseEvent<HTMLDivElement>) {

    if (disabled) return;

    clear();
    field?.clear();
    event?.stopPropagation();
  }

  function handleBlur() {
    setOpenPopup(false);
    field?.validate();
  }

  function handleOptionClick(value: any, label: string) {
    setOpenPopup(false);
    setDisplayValue(label);
    onChange?.(value);
    onSearch?.(label);
    field?.update(value);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {

    if (disabled) return;

    setDisplayValue(event.target.value);
    debounce(event.target.value, (value) => onSearch?.(value));
  }

  function clear() {
    setOpenPopup(false);
    setDisplayValue('');
    onSearch?.('');
    onChange?.(undefined);
  }

  return (
    <div className={clsx(style.container, disabled && style.disabled)}>
      {label && 
        <label className={style.label}>{label}</label>
      }
      <div 
        className={style.content}
        ref={ref} 
        onClick={handleClick}>
        {!hideIcon && 
          <Icon className={style.icon} icon={icon}/>
        }
        <input 
          className={style.input} 
          value={displayValue}
          disabled={disabled}
          type='text'
          placeholder={placeholder}
          autoFocus={autofocus}
          onChange={handleChange}/>
        {displayValue !== '' && 
          <Icon 
            className={style.clear} 
            icon='clear'
            onClick={handleClear}/>
        }
      </div>
      <FieldError message={field?.error ?? error}/>
      {hint && <Text className={style.hint} text={hint}/>}
      <Menu
        open={openPopup && Children.count(children) !== 0}
        anchor={ref} 
        top={8}
        autoWith={true}
        autoUpdatePosition={autoUpdatePosition}
        style={{ container: style.popup }}
        onClose={handleBlur}>
        {
          Children.map(children, (child: any, index) => (
            cloneElement(child, { 
              key: index,
              showClearIcon: false,
              selected: JSON.stringify(child.props.value) === JSON.stringify(value),
              onClick: handleOptionClick
            })
          ))
        }
      </Menu>
    </div>
  );
}
